/**
 * Turn a raw BERTopic label into a short, readable display name.
 *
 * Raw labels look like "42_course_courses_school_participants": a numeric id
 * followed by the top keywords joined with underscores (keywords may
 * themselves be multi-word phrases, e.g. "citizen science").
 *
 * Scheme:
 *   1. Drop the leading numeric id.
 *   2. Keep the first two distinct keyword representations.
 *   3. Skip a keyword that merely repeats one already kept — a plural
 *      ("course" vs "courses"), a near-identical word (≥90% similar), or a
 *      phrase sharing the same leading word ("citizen" vs "citizen science").
 *      When a repeat is a more descriptive multi-word phrase, it upgrades the
 *      kept keyword instead of being dropped.
 *   4. Capitalise each kept keyword.
 *
 * Display-only: the raw label is still used as the filter value everywhere,
 * so changing this never affects filtering or backend queries.
 *
 * Falls back to the original string when it doesn't match the expected shape.
 */

function firstWord(s) {
  return s.split(/\s+/)[0];
}

function bigrams(s) {
  const set = new Set();
  for (let i = 0; i < s.length - 1; i += 1) set.add(s.slice(i, i + 2));
  return set;
}

// Sørensen–Dice similarity on character bigrams (0–1).
function diceRatio(a, b) {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return a === b ? 1 : 0;
  const A = bigrams(a);
  const B = bigrams(b);
  let inter = 0;
  A.forEach((g) => { if (B.has(g)) inter += 1; });
  return (2 * inter) / (A.size + B.size);
}

function isPlural(short, long) {
  return long === `${short}s` || long === `${short}es`
    || (long.endsWith('s') && long.slice(0, -1) === short);
}

// True when `b` merely repeats `a` (plural, near-identical, or shared lead word).
function isDuplicateKeyword(a, b) {
  if (a === b) return true;
  if (firstWord(a) === firstWord(b)) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (isPlural(short, long)) return true;
  return diceRatio(a, b) >= 0.9;
}

export function clipTopicName(name) {
  if (!name || typeof name !== 'string') return name;

  // Strip stray leading/trailing hyphens left by the topic model ("infraia-")
  let tokens = name.split('_').map((t) => t.trim().replace(/^-+|-+$/g, '')).filter(Boolean);
  if (tokens.length && /^-?\d+$/.test(tokens[0])) tokens = tokens.slice(1);
  if (tokens.length === 0) return name;

  const picked = []; // original-case keywords
  for (const tok of tokens) {
    const lower = tok.toLowerCase();
    let dupIdx = -1;
    for (let i = 0; i < picked.length; i += 1) {
      if (isDuplicateKeyword(picked[i].toLowerCase(), lower)) { dupIdx = i; break; }
    }
    if (dupIdx >= 0) {
      // Upgrade only to a more descriptive (multi-word) phrase; otherwise keep
      // the existing keyword (favours the cleaner singular form).
      if (tok.split(/\s+/).length > picked[dupIdx].split(/\s+/).length) {
        picked[dupIdx] = tok;
      }
      continue;
    }
    if (picked.length >= 2) break;
    picked.push(tok);
  }

  return picked.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(' ');
}

